import { useTranslation } from "react-i18next";

import { AutoGrid } from "../app/layout/AutoGrid";
import { EmptyState } from "../components/EmptyState";
import { gateVisible } from "../app/gating";
import { HOME_WIDGETS } from "../app/home/widgets";
import { useSiteStore } from "../stores/siteStore";
import css from "../app/home/Home.module.css";

/**
 * The overview (ADR 274, stage 0 of ADR 273): what a manager wants to know
 * without opening a page — what is wrong, what the fleet is doing, what the last
 * count found.
 *
 * The screen is a **filtered list of widget ids**, not a hand-assembled layout.
 * Each widget declares the gate it needs and this page evaluates it with the
 * same function the nav uses, so a user never sees a tile for a capability their
 * site does not have or a role they do not hold. Stage 2 replaces the list with
 * a persisted one and changes nothing else.
 */
export function HomePage() {
  const { t } = useTranslation();
  const isGlobalAdmin = useSiteStore((s) => s.isGlobalAdmin);
  const roleRank = useSiteStore((s) => s.roleRank);
  // Subscribe to the sites array, not a derived boolean: a capability edit keeps
  // the site count but changes the caps, and a length-only subscription would
  // never re-render (the same trap the Sidebar documents, ADR 120/183).
  const availableSites = useSiteStore((s) => s.availableSites);
  const activeSiteId = useSiteStore((s) => s.activeSiteId);
  const activeWarehouseId = useSiteStore((s) => s.activeWarehouseId);
  const capabilitiesFor = useSiteStore((s) => s.capabilitiesFor);
  const canAdminOrgUnits = useSiteStore((s) => s.adminOrgUnits.length > 0);
  const isSales = useSiteStore((s) => s.isSales);

  const caps = capabilitiesFor(activeSiteId);
  const visible = HOME_WIDGETS.filter((w) =>
    gateVisible(w, {
      isGlobalAdmin,
      roleRank,
      hasCapability: (c) => caps[c],
      hasSite: availableSites.length > 0,
      canAdminOrgUnits,
      isSales,
    }));

  const siteName = availableSites.find((s) => s.id === activeSiteId)?.name;

  return (
    <div className={css.page}>
      <header className={css.head}>
        <h1 className={css.title}>{t("home.title")}</h1>
        {siteName && <p className={css.subtitle}>{siteName}</p>}
      </header>

      {visible.length === 0 ? (
        // Nothing passed the gate: either the system is not provisioned or this
        // site does nothing yet. Both are fixed in the same place.
        <EmptyState
          title={t("home.empty.title")}
          description={t("home.empty.desc")}
          action={{ to: "/setup", label: t("home.empty.cta") }}
        />
      ) : (
        <AutoGrid min={260}>
          {visible.map(({ id, size, Component }) => (
            <div key={id} className={size === "wide" ? css.wide : undefined}>
              <Component siteId={activeSiteId} warehouseId={activeWarehouseId} />
            </div>
          ))}
        </AutoGrid>
      )}
    </div>
  );
}
